<template>
    <div class="target-build-show">
        <b-notification v-if="error"
                        type="is-danger is-light"
                        aria-close-label="Close notification"
                        role="alert">{{ error }}</b-notification>
        <div class="target-build-show-data" v-else-if="!isLoading">
            <div class="title">{{ currentTargetBuild.product_name }}</div>
            <div class="subtitle">{{ currentTargetBuild.target }}</div>
            <div><b>started at:</b> {{ new Date(currentTargetBuild.started_at) | moment("dddd, MMMM Do YYYY, HH:mm") }}</div>
            <div v-if="currentTargetBuild.filesystem"><b>filesystem:</b> {{ currentTargetBuild.filesystem }}</div>
            <div><b>target:</b> {{ currentTargetBuild.target }}</div>
            <div><b>branch:</b> {{ currentTargetBuild.branch }}</div>
            <div><b>repository:</b> {{ currentTargetBuild.repository }}</div>
            <div><b>commit:</b> {{ currentTargetBuild.commit_id }}</div>
            <div v-if="currentTargetBuild.columnstore_commit_id"><b>columnstore commit:</b> {{ currentTargetBuild.columnstore_commit_id	 }}</div>
            <b-table
                ref="table"
                :data="currentMtrSummary[0].mtr_name == null ? [] : currentMtrSummary"
                striped
                narrowed
                hoverable
                detailed
                detail-key="mtr_name"
                @click="$refs.table.toggleDetails($event)"
                show-detail-icon>

                <b-table-column field="mtr_name" label="MTR" v-slot="props">
                        {{ props.row.mtr_name }}
                </b-table-column>

                <b-table-column field="results_count" label="Failed results" v-slot="props">
                    {{ props.row.results_count }}
                </b-table-column>

                <template #detail="props">
                  <LogsLinksTable :logs-urls="getMtrLogsUrls(props.row.mtr_name)"/>
                </template>

                <template #empty>
                    <div>No MTR found</div>
                </template>
            </b-table>
            <b-button @click="onDropTargetClick" type="is-danger" icon-left="delete">
                Drop target build
            </b-button>
            <b-button @click="onDropTestCasesClick" type="is-info" icon-left="delete">
              Drop test cases for target in date interval
            </b-button>
            <b-button type="is-primary"
                      v-if="failedTestCases && failedTestCases.length > 0" @click="onShowFailedTestsClick">
                All failed tests
            </b-button>
        </div>



        <b-loading :is-full-page="false" :active="isLoading"></b-loading>
        <b-modal :active="failedTestCasesIsShow" full-screen has-modal-card>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">All failed tests</p>
                    <button class="delete" aria-label="close" @click="onCloseFailedTests"></button>
                </header>
                <div class="modal-card-body">
                    <TargetBuildFailedTests :failed-test-cases="failedTestCases"
                                            :target-build="currentTargetBuild"/>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex';
    import api from "@/api/api";
    import TargetBuildFailedTests from "@/views/TargetBuildFailedTests";
    import LogsLinksTable from "@/components/LogsLinksTable";
    import {TIME_INTERVAL_OPTIONS} from "@/utils/constants";
    import {formatDate} from "@/utils/helpers";

    export default {
        name: "TargetBuildShow",
        props: {
            targetBuildId: [Number, String],
            dates: Array,
            timeInterval: [Number, String],
        },
        components: {
          LogsLinksTable,
            TargetBuildFailedTests,
        },
        watch: {
            targetBuildId() {
                this.fetchData();
            }
        },
        data() {
            return {
                isLoading: false,
                error: null,
                TIME_INTERVAL_OPTIONS,
            }
        },
        computed: {
            ...mapGetters('TargetBuilds', [
                'currentMtrSummary',
                'currentTargetBuild',
                'failedTestCases',
            ]),
            ...mapGetters('Auth', [
                'hasAccess',
            ]),
            failedTestCasesIsShow() {
                return [true, 'true'].includes(this.$route.query.failedTestCasesIsShow);
            }
        },
        methods: {
            ...mapActions('TargetBuilds', [
                'fetchMtrSummary',
                'fetchTargetBuild',
            ]),
            async fetchData() {
                this.isLoading = true;
                try {
                    await Promise.all([
                        this.fetchTargetBuild({ targetBuildId: this.targetBuildId }),
                        this.fetchMtrSummary({ targetBuildId: this.targetBuildId }),
                    ]);
                } catch(message) {
                    this.error = message;
                }
                this.isLoading = false;
            },
            async onDropTargetClick() {
                this.$buefy.dialog.confirm({
                    title: 'Deleting target build',
                    message: 'Are you sure you want to <b>delete</b> this target build? This action cannot be undone.',
                    confirmText: 'Delete target build',
                    type: 'is-danger',
                    hasIcon: true,
                    onConfirm: async () => {
                        await api.targetBuilds.dropTargetBuild(this.targetBuildId);
                        this.$buefy.toast.open('The target build has been added to the queue for deletion!');
                        this.$emit('close');
                    }
                });
            },
            async onDropTestCasesClick() {
              this.$buefy.dialog.confirm({
                title: 'Deleting test cases for target',
                message: `Are you sure you want to delete all test results in date interval for this target?`,
                confirmText: 'Delete test cases for target',
                type: 'is-danger',
                hasIcon: true,
                onConfirm: async () => {
                  await api.targetBuilds.dropTestCasesForTarget(this.targetBuildId, this.dates.map(date => formatDate(date)), this.timeInterval);
                  this.$buefy.toast.open('The test cases for target have been added to the queue for deletion!');
                  this.$emit('close');
                }
              });
            },
            onShowFailedTestsClick() {
                const query = Object.assign({}, this.$route.query, {
                    failedTestCasesIsShow: true,
                });
                this.$router.push({ name: this.$route.name, query });
            },
            onCloseFailedTests() {
                const query = this._.omit(this.$route.query, ['failedTestCasesIsShow']);
                this.$router.push({ name: this.$route.name, query });
            },
            getMtrLogsUrls(mtrName) {
                return this.currentTargetBuild.logs_urls.filter(log => log.mtr_name === mtrName).sort(
                    log => log.platform
                )
            }
        },
        created() {
            this.fetchData();
        }
    }
</script>

<style scoped>
    .target-build-show-data {
        display: flex;
        flex-direction: column;
    }
</style>